import React, { useState, useMemo, useEffect } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

// ─── Constantes ──────────────────────────────────────────────────────────────

export const SOURCE_COLORS = [
  "#2563eb", "#10b981", "#f59e0b", "#ef4444",
  "#6366f1", "#14b8a6", "#ec4899", "#f97316",
];

const MONTH_NAMES = {
  "01": "Enero",   "02": "Febrero", "03": "Marzo",    "04": "Abril",
  "05": "Mayo",    "06": "Junio",   "07": "Julio",    "08": "Agosto",
  "09": "Sep.",    "10": "Oct.",    "11": "Nov.",      "12": "Dic.",
};

const MONTH_SHORT = {
  "01": "Ene", "02": "Feb", "03": "Mar", "04": "Abr",
  "05": "May", "06": "Jun", "07": "Jul", "08": "Ago",
  "09": "Sep", "10": "Oct", "11": "Nov", "12": "Dic",
};

const CHART_MODES = [
  { key: "tendencia", icon: "bi-graph-up",  title: "Tendencia"    },
  { key: "barras",    icon: "bi-bar-chart", title: "Ranking"      },
  { key: "pastel",    icon: "bi-pie-chart", title: "Distribución" },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function buildLineRows(sourcesMonthly) {
  const periodsMap = {};
  const fuentesSet = new Set();
  sourcesMonthly.forEach(({ period, fuente, total }) => {
    if (!periodsMap[period]) periodsMap[period] = { period };
    periodsMap[period][fuente] = Number(total) || 0;
    fuentesSet.add(fuente);
  });
  const fuentes = Array.from(fuentesSet);
  const rows = Object.values(periodsMap).sort((a, b) => a.period.localeCompare(b.period));
  rows.forEach((row) => fuentes.forEach((f) => { if (row[f] === undefined) row[f] = 0; }));
  return { rows, fuentes };
}

function PieCustomLabel({ cx, cy, midAngle, outerRadius, percent, name }) {
  if (percent < 0.05) return null;
  const RAD = Math.PI / 180;
  const x = cx + (outerRadius + 22) * Math.cos(-midAngle * RAD);
  const y = cy + (outerRadius + 22) * Math.sin(-midAngle * RAD);
  return (
    <text x={x} y={y} fill="#374151" textAnchor={x > cx ? "start" : "end"} dominantBaseline="central" fontSize={11}>
      {name} {Math.round(percent * 100)}%
    </text>
  );
}

// ─── OrigenTendenciaChart ─────────────────────────────────────────────────────
// Props:
//   sources        — [{ fuente, total }]
//   sourcesMonthly — [{ period, fuente, total }]
//   cardClassName  — clase CSS del card wrapper (default: "recep-section-card")

export default function OrigenTendenciaChart({
  sources = [],
  sourcesMonthly = [],
  cardClassName = "recep-section-card",
}) {
  const [filterYear,  setFilterYear]  = useState("all");
  const [filterMonth, setFilterMonth] = useState("all");
  const [chartMode,   setChartMode]   = useState("tendencia");

  const availableYears = useMemo(() => {
    const years = new Set(sourcesMonthly.map((r) => r.period.split("-")[0]));
    return Array.from(years).sort((a, b) => b.localeCompare(a));
  }, [sourcesMonthly]);

  const availableMonths = useMemo(() => {
    if (filterYear === "all") return [];
    const months = new Set(
      sourcesMonthly
        .filter((r) => r.period.startsWith(filterYear))
        .map((r) => r.period.split("-")[1])
    );
    return Array.from(months).sort();
  }, [sourcesMonthly, filterYear]);

  // Al seleccionar un mes en modo tendencia → cambiar a barras automáticamente
  useEffect(() => {
    if (filterMonth !== "all" && chartMode === "tendencia") setChartMode("barras");
  }, [filterMonth]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleYearChange = (y) => {
    setFilterYear(y);
    setFilterMonth("all");
  };

  const filtered = useMemo(() => {
    return sourcesMonthly.filter((r) => {
      const [y, m] = r.period.split("-");
      if (filterYear !== "all" && y !== filterYear) return false;
      if (filterMonth !== "all" && m !== filterMonth) return false;
      return true;
    });
  }, [sourcesMonthly, filterYear, filterMonth]);

  // Totales por fuente para el periodo filtrado (barras + pastel)
  const aggregated = useMemo(() => {
    const totals = {};
    filtered.forEach(({ fuente, total }) => {
      totals[fuente] = (totals[fuente] || 0) + Number(total);
    });
    return Object.entries(totals)
      .map(([fuente, total]) => ({ fuente, total }))
      .sort((a, b) => b.total - a.total);
  }, [filtered]);

  const { rows: lineRows, fuentes } = useMemo(() => buildLineRows(filtered), [filtered]);

  const fuenteColorMap = useMemo(() => {
    const map = {};
    sources.forEach((s, i) => { map[s.fuente] = SOURCE_COLORS[i % SOURCE_COLORS.length]; });
    return map;
  }, [sources]);

  const topSource = sources.length > 0
    ? [...sources].sort((a, b) => Number(b.total) - Number(a.total))[0]
    : null;

  const effectiveMode = (filterMonth !== "all" && chartMode === "tendencia") ? "barras" : chartMode;
  const hasData = effectiveMode === "tendencia" ? lineRows.length > 0 : aggregated.length > 0;

  const subtituloMap = {
    tendencia: filterYear !== "all" ? `Tendencia mensual ${filterYear}` : "Evolución — últimos 12 meses",
    barras: filterMonth !== "all"
      ? `${MONTH_NAMES[filterMonth] || filterMonth} ${filterYear} — ranking`
      : filterYear !== "all" ? `Ranking acumulado ${filterYear}` : "Ranking total por fuente",
    pastel: filterMonth !== "all"
      ? `Distribución ${MONTH_NAMES[filterMonth] || filterMonth} ${filterYear}`
      : filterYear !== "all" ? `Distribución ${filterYear}` : "Distribución total",
  };

  return (
    <div className={`card ${cardClassName} h-100`}>
      <div className="card-header bg-white border-0">

        {/* Título + badge top */}
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
          <div>
            <h6 className="mb-0">Origen de Interesados</h6>
            <small className="text-muted">{subtituloMap[effectiveMode]}</small>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            {topSource && (
              <span className="badge rounded-pill" style={{ background: "#fef3c7", color: "#92400e", fontSize: "0.78rem", fontWeight: 600 }}>
                <i className="bi bi-lightning-fill me-1" />
                Top: {topSource.fuente} ({topSource.total})
              </span>
            )}
            <span className="dashboard-chip">
              <i className="bi bi-funnel me-1" />
              {sources.length} fuente{sources.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>

        {/* Filtros + toggle en la misma fila */}
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <select
            className="form-select form-select-sm"
            style={{ maxWidth: 116 }}
            value={filterYear}
            onChange={(e) => handleYearChange(e.target.value)}
          >
            <option value="all">Todos los años</option>
            {availableYears.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>

          <select
            className="form-select form-select-sm"
            style={{ maxWidth: 134 }}
            value={filterMonth}
            onChange={(e) => setFilterMonth(e.target.value)}
            disabled={filterYear === "all"}
          >
            <option value="all">Todos los meses</option>
            {availableMonths.map((m) => (
              <option key={m} value={m}>{MONTH_NAMES[m] || m}</option>
            ))}
          </select>

          {(filterYear !== "all" || filterMonth !== "all") && (
            <button
              type="button"
              className="btn btn-sm btn-light"
              onClick={() => { setFilterYear("all"); setFilterMonth("all"); }}
            >
              <i className="bi bi-x" />
            </button>
          )}

          {/* Toggle tipo gráfico — limpian filtros */}
          <div className="btn-group btn-group-sm ms-auto" role="group">
            {CHART_MODES.map(({ key, icon, title }) => (
              <button
                key={key}
                type="button"
                title={title}
                onClick={() => {
                  setFilterYear("all");
                  setFilterMonth("all");
                  setChartMode(key);
                }}
                className={`btn ${effectiveMode === key ? "btn-primary" : "btn-outline-secondary"}`}
                style={{ fontSize: "0.8rem", padding: "0.25rem 0.65rem" }}
              >
                <i className={`bi ${icon}`} />
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="p-3 pt-2">
        {!hasData ? (
          <div className="d-flex align-items-center justify-content-center text-muted" style={{ minHeight: 220 }}>
            <div className="text-center">
              <i className="bi bi-bar-chart d-block mb-2" style={{ fontSize: "1.8rem", opacity: 0.35 }} />
              Sin datos para el periodo seleccionado
            </div>
          </div>
        ) : effectiveMode === "tendencia" ? (
          /* ── Líneas: evolución mensual ── */
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={lineRows} margin={{ top: 8, right: 16, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="period"
                tickFormatter={(p) => { const [, mm] = (p || "").split("-"); return MONTH_SHORT[mm] || p; }}
                tick={{ fontSize: 11, fill: "#6b7280" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#6b7280" }} axisLine={false} tickLine={false} />
              <Tooltip
                formatter={(value, name) => [value, name]}
                labelFormatter={(label) => {
                  const [year, mm] = (label || "").split("-");
                  return `${MONTH_NAMES[mm] || mm} ${year}`;
                }}
                contentStyle={{ borderRadius: 8, border: "1px solid #e5e7eb", fontSize: "0.82rem" }}
              />
              <Legend wrapperStyle={{ fontSize: "0.8rem", paddingTop: 6 }} />
              {fuentes.map((fuente, idx) => (
                <Line
                  key={fuente}
                  type="monotone"
                  dataKey={fuente}
                  stroke={fuenteColorMap[fuente] || SOURCE_COLORS[idx % SOURCE_COLORS.length]}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        ) : effectiveMode === "barras" ? (
          /* ── Barras horizontales: ranking ── */
          <ResponsiveContainer width="100%" height={Math.max(120, aggregated.length * 32)}>
            <BarChart data={aggregated} layout="vertical" margin={{ top: 4, right: 32, left: 4, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: "#6b7280" }} axisLine={false} tickLine={false} />
              <YAxis
                type="category"
                dataKey="fuente"
                width={92}
                tick={{ fontSize: 12, fill: "#374151" }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                formatter={(v) => [v, "Interesados"]}
                contentStyle={{ borderRadius: 8, border: "1px solid #e5e7eb", fontSize: "0.82rem" }}
              />
              <Bar dataKey="total" barSize={18} radius={[0, 6, 6, 0]}>
                {aggregated.map((entry) => (
                  <Cell key={entry.fuente} fill={fuenteColorMap[entry.fuente] || SOURCE_COLORS[0]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          /* ── Donut: distribución proporcional ── */
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Tooltip
                formatter={(v, name) => [v, name]}
                contentStyle={{ borderRadius: 8, border: "1px solid #e5e7eb", fontSize: "0.82rem" }}
              />
              <Pie
                data={aggregated}
                dataKey="total"
                nameKey="fuente"
                cx="50%"
                cy="50%"
                innerRadius={52}
                outerRadius={95}
                paddingAngle={2}
                labelLine={false}
                label={PieCustomLabel}
              >
                {aggregated.map((entry) => (
                  <Cell key={entry.fuente} fill={fuenteColorMap[entry.fuente] || SOURCE_COLORS[0]} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        )}

        {/* Totales acumulados por fuente */}
        {sources.length > 0 && (
          <div className="d-flex flex-wrap gap-2 mt-3">
            {[...sources].sort((a, b) => Number(b.total) - Number(a.total)).map((s) => (
              <span
                key={s.fuente}
                className="badge rounded-pill"
                style={{
                  background: `${fuenteColorMap[s.fuente] || SOURCE_COLORS[0]}18`,
                  color: fuenteColorMap[s.fuente] || SOURCE_COLORS[0],
                  border: `1px solid ${fuenteColorMap[s.fuente] || SOURCE_COLORS[0]}44`,
                  fontSize: "0.78rem",
                  fontWeight: 600,
                }}
              >
                {s.fuente}: {s.total}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
